import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Form, Formik } from 'formik';

import Button from 'components/Common/Button/Button';
import FormikErrorsGtmLogger from 'components/Common/FormikErrorsGtmLogger/FormikErrorsGtmLogger';
import { ExtraStyleType } from 'components/Common/Formik/FieldCustom/FieldCustom';
import ScrollToFieldError from 'components/Common/Formik/ScrollToFieldError/ScrollToFieldError';
import TextFieldCustom from 'components/Common/Formik/TextFieldCustom/TextFieldCustom';
import Loader from 'components/Common/Loader/Loader';
import useGtmPageView from 'hooks/gtm/useGtmPageView';
import { useCustomer } from 'hooks/useCustomer';
import useEnv from 'hooks/useEnv';
import { validationSchema } from 'schemas/ResetPasswordErrors';
import { fetcherWithBody } from 'services/fetcher';
import { getRoutesUri } from 'services/generic';
import { GTM_PAGE_OTHER } from 'settings/gtm/gtmPageView';

import styles from './ResetPassword.module.scss';

type FormValues = {
  password: string;
  confirmPassword: string;
};

export default function ResetPassword(): JSX.Element {
  const queryParameters = new URLSearchParams(window.location.search);
  const token = queryParameters.get('token');

  const { t } = useTranslation(['resetPassword']);
  const [timestampValid, setTimestampValid] = useState(!!token);
  const [once, setOnce] = useState(false);
  const { logout } = useCustomer();
  const { urlApiCustomer } = useEnv();
  const navigate = useNavigate();
  const [loader, setLoader] = useState(true);

  useGtmPageView(GTM_PAGE_OTHER, '');

  const initialValues: FormValues = {
    password: '',
    confirmPassword: '',
  };

  useEffect(() => {
    if (token === null) {
      navigate(getRoutesUri('customerLogin') as string);

      return;
    }

    const controller = new AbortController();
    const signal = controller.signal;

    if (token && token.length > 0) {
      fetcherWithBody(`${urlApiCustomer}/checkTimestamp`, { token }, false, {
        signal,
      })
        .then(response => {
          setTimestampValid(response.statusCode == 200);
        })
        .then(() => {
          setOnce(true);
          setLoader(false);
        });
    }

    return () => {
      controller?.abort();
    };
  }, []);

  const onSubmit = (values: FormValues) => {
    fetcherWithBody(`${urlApiCustomer}/resetPassword`, {
      token,
      ...values,
    }).then(response => {
      if (response.message === 'OK') {
        logout().then(() => {
          localStorage.removeItem('user_login_type');
          navigate(getRoutesUri('customerLogin') as string, {
            state: { redirectResetPassword: true },
          });
        });
      }
    });
  };

  const NewPasswordExtraStyle: ExtraStyleType = {
    container: styles.fieldPasswordRequired,
    fieldContainer: styles.fieldContainer,
    label: styles.fieldLabel,
  };

  const ConfirmPasswordExtraStyle: ExtraStyleType = {
    container: styles.fieldConfirmationRequired,
    fieldContainer: styles.fieldContainer,
    label: styles.fieldLabel,
  };

  if (loader) {
    return (
      <>
        <RenderMetadata />
        <Loader />
      </>
    );
  }

  return (
    <>
      <RenderMetadata />
      {once && timestampValid ? (
        <main className={styles.mainContent}>
          <div className={styles.pageTitle}>
            <label>{t('RESET_MY_PASSWORD')}</label>
          </div>
          <div className={styles.content}>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
            >
              {
                <Form className={styles.form} noValidate autoComplete="false">
                  <FormikErrorsGtmLogger
                    page_type="identification"
                    form_type="réinitialisation du mot de passe"
                  />
                  <div className={styles.fieldPasswordBlock}>
                    <TextFieldCustom
                      id="PasswordWithHelper"
                      extraStyle={NewPasswordExtraStyle}
                      required
                      type="password"
                      label={t('LABEL_NEW_PASSWORD')}
                      name="password"
                      placeholder={t('LABEL_NEW_PASSWORD_PLACEHOLDER')}
                      style={{ flexGrow: 1 }}
                    />
                    <TextFieldCustom
                      extraStyle={ConfirmPasswordExtraStyle}
                      required
                      type="password"
                      label={t('LABEL_CONFIRM_NEW_PASSWORD')}
                      name="confirmPassword"
                      placeholder={t('LABEL_CONFIRMPASSWORD_PLACEHOLDER')}
                      style={{ flexGrow: 1 }}
                    />
                  </div>
                  <div className={styles.buttonField}>
                    <Button extraStyle={styles.button}>
                      {t('LABEL_SEND')}
                    </Button>
                  </div>
                  <ScrollToFieldError />
                </Form>
              }
            </Formik>
            <div className={styles.requiredField}>
              <label>
                <span>*</span>
                <span>{t('LABEL_REQUIRED_INFORMATION')}</span>
              </label>
            </div>
          </div>
        </main>
      ) : (
        once &&
        !timestampValid && (
          <main className={styles.mainContent}>
            <div className={styles.pageTitle}>
              <label>{t('RESET_MY_PASSWORD')}</label>
            </div>
            <div className={styles.content}>
              <div className={styles.expired}>
                <label>{t('LABEL_RESET_PASSWORD_EXPIRED')}</label>
              </div>
            </div>
          </main>
        )
      )}
    </>
  );
}

function RenderMetadata(): JSX.Element {
  const { t } = useTranslation(['resetPassword']);

  return (
    <Helmet>
      <title>
        {t('META_TITLE_TEMPLATE', {
          title: t('RESET_PASSWORD_META_TITLE'),
          ns: 'translation',
        })}
      </title>
    </Helmet>
  );
}
